import { useQueryCache } from "@pinia/colada";
import { useStorage } from "@vueuse/core";
import i18n from "i18next";
import rest from "@/api/rest";
import { toaster } from "@/components/providers/ToasterProvider";
import agentsEn from "@/locales/en/agents.json";
import apiKeysEn from "@/locales/en/api-keys.json";

import authEn from "@/locales/en/auth.json";

import commonEn from "@/locales/en/common.json";
import dashboardEn from "@/locales/en/dashboard.json";

import errorsEn from "@/locales/en/errors.json";

import knowledgeEn from "@/locales/en/knowledge.json";
import oauthEn from "@/locales/en/oauth.json";
import playgroundEn from "@/locales/en/playground.json";

import pricingEn from "@/locales/en/pricing.json";
import structuredAnalysisEn from "@/locales/en/structured-analysis.json";
import subscriptionLimitsEn from "@/locales/en/subscription-limits.json";
import subscriptionEn from "@/locales/en/subscription.json";

import webhookEn from "@/locales/en/webhook.json";
import agentsPt from "@/locales/pt/agents.json";

import apiKeysPt from "@/locales/pt/api-keys.json";
import authPt from "@/locales/pt/auth.json";


import commonPt from "@/locales/pt/common.json";

import dashboardPt from "@/locales/pt/dashboard.json";
import errorsPt from "@/locales/pt/errors.json";


import knowledgePt from "@/locales/pt/knowledge.json";
import oauthPt from "@/locales/pt/oauth.json";

import playgroundPt from "@/locales/pt/playground.json";
import pricingPt from "@/locales/pt/pricing.json";

import structuredAnalysisPt from "@/locales/pt/structured-analysis.json";
import subscriptionLimitsPt from "@/locales/pt/subscription-limits.json";
import subscriptionPt from "@/locales/pt/subscription.json";
import webhookPt from "@/locales/pt/webhook.json";
import { SETTINGS_QUERY_KEYS } from "@/queries/settings.query";
import { emitter } from "@/services/emitter";
import { notifyError } from "@/utils/errors";
import { api } from "./api";

const en = {
  auth: authEn,
  errors: errorsEn,
  common: commonEn,
  "api-keys": apiKeysEn,
  dashboard: dashboardEn,
  playground: playgroundEn,
  pricing: pricingEn,

  agents: agentsEn,

  knowledge: knowledgeEn,
  oauth: oauthEn,
  webhook: webhookEn,
  "structured-analysis": structuredAnalysisEn,
  subscription: subscriptionEn,
  "subscription-limits": subscriptionLimitsEn,
};

const pt = {
  auth: authPt,
  errors: errorsPt,
  common: commonPt,
  "api-keys": apiKeysPt,
  dashboard: dashboardPt,
  playground: playgroundPt,
  pricing: pricingPt,

  agents: agentsPt,

  knowledge: knowledgePt,
  oauth: oauthPt,
  webhook: webhookPt,
  "structured-analysis": structuredAnalysisPt,
  subscription: subscriptionPt,
  "subscription-limits": subscriptionLimitsPt,
};

export const resources = {
  pt: { ...pt },
  en: { ...en },
} as const;

export function initI18n() {
  i18n.init({
    fallbackLng: localStorage.getItem("locale") || "en",
    resources,
    defaultNS: "common",
    fallbackNS: "common",
    // Vue escapes text nodes itself; i18next's HTML-escaping would render
    // interpolated dates as "16&#x2F;08".
    interpolation: { escapeValue: false },
  });
}

type T = typeof i18n.t;
type TParams = Parameters<T>;

type Locale = "en" | "pt";
export const locale = useStorage<Locale>("locale", "pt");
export const getLocale = () => locale.value;

export const $t = ((...params: TParams): string => {
  // IMPORTANT!!! used to trigger reactivity
  locale.value;

  const response = i18n.t(...params);

  return response as string;
}) as T;

/**
 * Returns translated string or null if translation doesn't exist.
 * When i18n returns the key itself, it means no translation was found.
 *
 * @param key - Translation key to check
 * @returns Translated string or null if not found
 *
 * @example
 * const desc = $tOptional("pricing:rule_descriptions.voice");
 * if (desc) {
 *   // Show description
 * }
 */
export function $tOptional(key: string): string | null {
  const translated = $t(key as any, {
    defaultValue: "",
  });

  return translated ?? null;
}

export async function setLocale(lang: Locale, options?: { skipSync?: boolean }) {
  locale.value = lang;
  i18n.changeLanguage(lang);
  api.defaults.headers["Accept-Language"] = lang;

  // Se skipSync for true, não mostrar o toast
  if (options?.skipSync) {
    return;
  }

  try {
    // Buscar configurações atuais do usuário para verificar se o idioma mudou
    const { data: currentSettings } = await api.get("/settings/user");

    // Só mostrar o toast se o idioma selecionado for diferente do configurado
    if (currentSettings.language === lang) {
      return;
    }

    // Mostrar toast perguntando se quer sincronizar com configurações do usuário
    toaster.info({
      description: $t("language_sync.message"),
      duration: 10000, // 10 segundos para dar tempo de ler
      action: {
        label: $t("language_sync.sync_button"),
        handler: async (close) => {
          try {
            // Fazer update preservando apenas os campos permitidos (full_name, language)
            await rest.settings.updateUserSettings({
              full_name: currentSettings.full_name,
              language: lang,
            });

            // Invalidate settings cache to trigger refetch in components
            const queryCache = useQueryCache();
            queryCache.invalidateQueries({ key: SETTINGS_QUERY_KEYS.user() as any });

            // Emitir evento para back-compatibility (mantém compatibilidade temporária)
            emitter.emit("language-synced", { language: lang });

            toaster.success($t("language_sync.synced_success"));
          } catch (error) {
            notifyError(error);
          }
          close();
        },
      },
    });
  } catch (error) {
    // Se não conseguir buscar as configurações, não mostrar o toast para evitar erros
    console.warn("Could not fetch user settings for language sync:", error);
  }
}

export function getI18n(): T {
  return i18n.t.bind(i18n);
}
